import { useEffect, useMemo } from 'react';

import { WhereClause, WherePredicateOperator } from '@people-science/query-core';

import mannWhitneyU from '../../common/mannwhitneyu';
import {
  StudyParticipantQueryData,
  StudyParticipantQueryParamsDto,
  useQueryStudyParticipantData
} from './useQueryStudyParticipantData';
import { isAfter } from 'date-fns';
import { mergeMedicationSurveyData } from '../../common/mergeMedicationSurveyData';

export enum ImpactGaugeMeasurementAlgorithm {
  ManWhitneyUTest = 'Mann-Whitney U-Test',
  ManWhitneyUTestBaselineAverage = 'Mann-Whitney U-Test Baseline Average',
  ManWhitneyUTestSampleTimeframe = 'Mann-Whitney U-Test Sample Timeframe'
}

export interface ImpactMeasurement {
  entityId?: string;
  columnName?: string;
  wheres?: WhereClause[];
  /**
   * Use algorithm instead
   * @deprecated
   */
  enableBaselineAverage?: boolean;
  baselineAverage?: number;
  algorithm?: ImpactGaugeMeasurementAlgorithm;
  initialBaselineSampleDayCount?: number;
  comparedLastSampleDayCount?: number;
}

export function useQueryStudyParticipantImpact(
  studyParticipantId: number,
  measurements: ImpactMeasurement[]
) {
  const { queryResults, loadingQuery, loadingQueryError, queryData } =
    useQueryStudyParticipantData(studyParticipantId);
  // build query from component configuration
  useEffect(() => {
    // important to check measurement.length as measurements gets initialized as
    // empty array and can cause useEffect to infinitely loop.
    if (!studyParticipantId || !measurements?.length) return;
    const params: StudyParticipantQueryParamsDto[] = [];
    for (const measurement of measurements) {
      if (measurement.entityId && measurement.columnName) {
        const whereColumns: string[] = [];
        for (const where of measurement.wheres || []) {
          const predicateCondition = where.condition as WherePredicateOperator;
          if (predicateCondition.parameters) {
            // TODO: account for the different operator types and params
            const path = predicateCondition.parameters[0];
            whereColumns.push(path.split('.')[1]);
          }
        }
        params.push({
          entityId: measurement.entityId,
          columnNames: [measurement.columnName, ...whereColumns]
          // wheres: measurement.wheres
        });
      }
    }
    queryData(params);
  }, [queryData, studyParticipantId, measurements]);
  // get array of measurement P values
  const impactResults = useMemo(() => {
    if (!queryResults) return [];
    const res = mergeMedicationSurveyData(queryResults);
    return res.map((queryData, index) => {
      const onMedicationData: number[] = [];
      const offMedicationData: number[] = [];
      let averageOnMedication = 0;
      let averageOffMedication = 0;
      const measurement = measurements[index];
      // get on/off medication data
      for (let index = 0; index < queryData.length; index++) {
        const item = queryData[index];
        const dataPoint = item[measurement.columnName!];
        // Check if on/off medication data
        if (isOnMedication(item, measurement.wheres || [])) {
          onMedicationData.push(dataPoint);
          averageOnMedication += dataPoint;
          if (
            measurement.algorithm ===
              ImpactGaugeMeasurementAlgorithm.ManWhitneyUTestSampleTimeframe &&
            measurement.comparedLastSampleDayCount
          ) {
            if (
              onMedicationData.length > measurement.comparedLastSampleDayCount
            ) {
              averageOnMedication -= onMedicationData[0];
              onMedicationData.shift();
            }
          } else if (
            (measurement.enableBaselineAverage ||
              measurement.algorithm ===
                ImpactGaugeMeasurementAlgorithm.ManWhitneyUTestBaselineAverage) &&
            typeof measurement.baselineAverage === 'number'
          ) {
            if (onMedicationData.length > measurement.baselineAverage) {
              averageOnMedication -= onMedicationData[0];
              onMedicationData.shift();
            }
          }
        } else {
          if (
            (measurement.enableBaselineAverage ||
              measurement.algorithm ===
                ImpactGaugeMeasurementAlgorithm.ManWhitneyUTestBaselineAverage) &&
            typeof measurement.baselineAverage === 'number'
          ) {
            // add first off-med data point to balance out on-med data
            // for (let i = 0; i < measurement.baselineAverage; i++) {
            //   offMedicationData.push(dataPoint);
            //   averageOffMedication += dataPoint;
            // }
            offMedicationData.push(dataPoint);
            averageOffMedication += dataPoint;
            if (offMedicationData.length > measurement.baselineAverage) {
              averageOffMedication -= offMedicationData[0];
              offMedicationData.shift();
            }
          } else {
            if (
              measurement.algorithm ===
                ImpactGaugeMeasurementAlgorithm.ManWhitneyUTestSampleTimeframe &&
              measurement.initialBaselineSampleDayCount
            ) {
              if (
                offMedicationData.length <
                measurement.initialBaselineSampleDayCount
              ) {
                offMedicationData.push(dataPoint);
                averageOffMedication += dataPoint;
              }
            } else {
              offMedicationData.push(dataPoint);
              averageOffMedication += dataPoint;
            }
          }
        }
      }
      averageOnMedication /= onMedicationData.length;
      averageOffMedication /= offMedicationData.length;
      // Get P value
      // console.log('onMedicationData', onMedicationData);
      // console.log('offMedicationData', offMedicationData);
      const result = mannWhitneyU.test(onMedicationData, offMedicationData);
      return {
        ...result,
        averageOnMedication,
        averageOffMedication,
        averageChange: averageOnMedication - averageOffMedication
      };
    });
    // calculate average change
  }, [measurements, queryResults]);
  // console.log('impactResults', impactResults);
  return {
    impactResults,
    loadingImpact: loadingQuery,
    loadingImpactError: loadingQueryError
  };
}

const isOnMedication = (
  data: StudyParticipantQueryData<any>,
  wheres: WhereClause[]
) => {
  // console.log('data', data, wheres);
  let value = '';
  for (const where of wheres) {
    const predicateCondition = where.condition as WherePredicateOperator;
    if (predicateCondition.parameters) {
      const path = predicateCondition.parameters[0];
      const fieldKey = path.split('.')[1];
      value = data[fieldKey];

      // if any of the conditions are try then continue checking in the loop.
      // if all passes then user is on medication, else return false;
      const isOnMed = (() => {
        switch (predicateCondition.operator) {
          case 'lessThan':
            return value < predicateCondition.parameters[1];
          case 'lessThanOrEqual':
            return value <= predicateCondition.parameters[1];
          case 'moreThan':
            return value > predicateCondition.parameters[1];
          case 'moreThanOrEqual':
            return value < predicateCondition.parameters[1];
          case 'equal': {
            const propValue = predicateCondition.parameters[1];
            // perform non-equality check `==`
            if (typeof propValue === 'boolean') {
              if (typeof value === 'string') {
                return (
                  (propValue && value === 'true') ||
                  (!propValue && value === 'false')
                );
              } else {
                return !!value == propValue;
              }
            } else {
              return value == propValue;
            }
          }
          case 'notEqual': {
            const propValue = predicateCondition.parameters[1];
            // perform non-equality check `==`
            if (typeof propValue === 'boolean') {
              if (typeof value === 'string') {
                return (
                  (propValue && value !== 'true') ||
                  (!propValue && value !== 'false')
                );
              }
              return !!value != propValue;
            } else {
              return value != propValue;
            }
          }
          case 'fulltext':
          case 'ilike':
            return value
              .toString()
              .toLowerCase()
              .includes(predicateCondition.parameters[1]);
          case 'like':
            return value.toString().includes(predicateCondition.parameters[1]);
          case 'between':
            return (
              value >= predicateCondition.parameters[1] &&
              value <= predicateCondition.parameters[2]
            );
          case 'any':
          case 'in': {
            const [, ...matches] = predicateCondition.parameters.map(param =>
              // map to true/false string if boolean comparison
              typeof param === 'boolean' && typeof value === 'string'
                ? param
                  ? 'true'
                  : 'false'
                : param
            );
            return matches.includes(value);
          }
          case 'isNull':
            return !value;
        }
      })();
      if (!isOnMed) return false;
    } else {
      // TODO: account for the different operator types and params
      throw new Error('Check complex where clause');
    }
  }
  return true;
};
